<template>
  <div
    :id="id"
    v-touch="onTouch"
    class="branch"
    :class="classObject"
    :style="styles"
    @contextmenu.prevent="onContextMenu"
    @mouseup="onMouseUp"
    @mousedown="onMouseDown"
  >
    <div
      v-if="canEdit"
      ref="leftTarget"
      class="connection-handler left"
      data-handler="left"
    />
    <div
      v-if="canEdit"
      ref="topTarget"
      class="connection-handler up"
      data-handler="top"
    />
    <div
      v-if="canEdit"
      ref="bottomTarget"
      class="connection-handler down"
      data-handler="bottom"
    />
    <div class="left" />
    <div ref="right" class="right">
      <board-branch-condition
        :id="branch.conditions.ifCondition"
        ref="ifCondition"
        :type="'ifCondition'"
        :branch-id="id"
        @disable-branch="onDisableBranch"
        @enable-branch="onEnableBranch"
      />
      <board-branch-condition
        v-for="elseIfCondition in branch.conditions.elseIfConditions"
        :id="elseIfCondition"
        :key="elseIfCondition"
        ref="elseIfConditions"
        :type="'elseIfCondition'"
        :branch-id="id"
        @disable-branch="onDisableBranch"
        @enable-branch="onEnableBranch"
      />
      <board-branch-condition
        v-if="branch.conditions.elseCondition"
        :id="branch.conditions.elseCondition"
        ref="elseCondition"
        :type="'elseCondition'"
        :branch-id="id"
        @disable-branch="onDisableBranch"
        @enable-branch="onEnableBranch"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';

import nodeOnTouch from '@/components/mixins/nodeOnTouch.js';
import jspSingleton from '@/lib/jspSingleton.js';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import misc from '../../mixins/misc.js';
import updater from '../../../vuex/project/updater.js';
import BoardBranchCondition from './BoardBranchCondition.vue';
import resizeObserverWrapper from '../../resize-observer-wrapper.js';

export default {
  components: {
    BoardBranchCondition,
  },

  mixins: [misc, nodeOnTouch],

  props: {
    id: {
      type: String,
      default: null,
    },
    virtualSelected: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      dragEnter: false,
      allowedTypes: [],
      tlist: [],
      dragCounter: 0,
      themes: {
        default: '#354042',
        red: '#571400',
        blue: '#002e6b',
        purple: '#540093',
        cyan: '#006557',
        green: '#00500b',
        pink: '#69003f',
        gold: '#5d5a00',
        orange: '#742b00',
        lightBlue: '#005186',
        brown: '#563400',
        moss: '#386300',
      },
    };
  },

  computed: {
    ...mapGetters('project/data/elements', { getElement: 'getNode' }),
    ...mapGetters('project/data/jumpers', { getJumper: 'getNode' }),
    ...mapGetters('project/data/notes', { getNote: 'getNode' }),
    ...mapGetters('project/data/branches', { getBranch: 'getNode' }),
    ...mapGetters('project/data/comments', { getComment: 'getNode' }),
    ...mapGetters('project/config/elements', {
      getIsElementSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/notes', {
      getIsNoteSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/jumpers', {
      getIsJumperSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/branches', {
      getIsSelected: 'getIsSelected',
      getIsBranchSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/connections', {
      getIsConnectionSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('application/channels', ['getCollaborators']),
    ...mapGetters('project/config/boards', ['getOpen']),
    ...mapGetters('project/data/boards', { getBoard: 'getNode' }),
    ...mapGetters('application/drag', ['getItems', 'getTarget', 'getIsEmpty']),
    ...mapGetters('project/config', ['getGrid']),
    ...mapGetters('application', ['getExactTransform']),

    theme() {
      if (!this.branch) return '#0d0e0e';
      const hasColor = _.has(this.themes, this.branch.theme);
      if (hasColor) {
        return this.themes[this.branch.theme];
      }
      return this.themes.default;
    },

    /**
     * Return the branch
     * @returns {Object}    The branch object
     */
    branch() {
      return this.getBranch(this.id);
    },

    /**
     * Return the css styles for positioning
     * @returns {Object}    The { left, top, --user-selected-color } styles
     */
    styles() {
      const { x, y } = this.branch || { x: 0, y: 0 };
      return {
        left: `${x}px`,
        top: `${y}px`,
        '--user-selected-color': this.selectedByCollaborator?.color,
      };
    },

    /**
     * Return the connection handler css styles for scaling
     * @returns {Object}    The { transform } styles
     */
    // connectionHandlerStyles() {
    //   const distance = 16 / this.scale;
    //   return {
    //     width: `${distance}px`,
    //     height: `${distance}px`,
    //     left: `${-distance}px`,
    //     right: `${-distance}px`,
    //     top: `${-distance}px`,
    //     bottom: `${-distance}px`,
    //     // 'border-radius': `${8 / this.scale}px`,
    //   };
    // },

    /**
     * Get the scale of the exact transform
     * @returns {Number}
     */
    scale() {
      return this.getExactTransform.scale;
    },

    /**
     * Return the collaboarator that has selected this
     */
    selectedByCollaborator() {
      const collaborators = this.getCollaborators.filter(
        c =>
          c.selections &&
          c.selections.branches &&
          c.selections.branches.includes(this.id)
      );
      if (collaborators.length > 0) return collaborators[0];
      return null;
    },

    /**
     * Return whether the current branch is selected
     * @returns {Boolean}   Whether it is selected
     */
    isSelected() {
      return this.getIsSelected(this.id);
    },

    /**
     * Return whether there is something currently selected
     * @returns {Boolean}   Whether there is something selected
     */
    isSelectionEmpty() {
      return (
        this.getIsElementSelectionEmpty &&
        this.getIsConnectionSelectionEmpty &&
        this.getIsNoteSelectionEmpty &&
        this.getIsJumperSelectionEmpty &&
        this.getIsBranchSelectionEmpty
      );
    },

    /**
     * Return the dynamic classes for the branch
     * @returns {Object}
     */
    classObject() {
      return {
        [this.branch.theme]: true,
        'jtk-drag-selected': this.isSelected,
        'user-selected': this.selectedByCollaborator !== null,
        'virtual-selected': this.virtualSelected,
      };
    },
  },
  watch: {
    /**
     * Watch selection change to inform jsplumb and enable-disable resizable.
     */
    isSelected(newVal, oldVal) {
      console.log('--Branch selection changed to:', newVal);
      if (!this.canEdit) return;
      if (newVal) {
        jspSingleton.instance.addToDragSelection(this.$el); // Add it to jsplumb selection
      } else {
        jspSingleton.instance.removeFromDragSelection(this.$el);
      }
    },
    /**
     * Some property in the element style changes
     */
    styles(newVal, oldVal) {
      this.$nextTick(() => {
        jspSingleton.instance.revalidate(this.$el); // we need to repaint the element's connections
      });
    },

    /**
     * There is nothing being dragged
     */
    getIsEmpty(newVal, oldVal) {
      if (newVal) {
        // Clear any drag enter
        this.dragEnter = false;
      }
    },

    /**
     * An else condition is being added or removed
     * Temporarily unmakeTarget or else jsplumb messes the condition endpoint's drop target area
     */
    'branch.conditions.elseCondition': function elseCondition(newVal, oldVal) {
      // if (jspSingleton.instance.isTarget(this.$el)) {
      //   console.log('UNMAKE TARGET on branch (elseCondition):', this.id);
      //   jspSingleton.instance.unmakeTarget(this.$el);
      // }
    },

    /**
     * An elseIf condition is being added or removed
     * Temporarily unmakeTarget or else jsplumb messes the condition endpoint's drop target area
     */
    'branch.conditions.elseIfConditions': function m(newVal, oldVal) {
      // if (jspSingleton.instance.isTarget(this.$el) && newVal !== oldVal) {
      //   jspSingleton.instance.unmakeTarget(this.$el);
      // }
    },
  },

  /**
   * Lifecycle hook, the branch element has been mounted
   */
  mounted() {
    jspSingleton.instance.setDraggable(this.$el, this.canEdit);
    // Make branch draggable only if it is editable
    if (!this.canEdit) return;

    // These listeners have to be added explicitly since vddl consumes them
    this.$el.addEventListener('dragenter', this.onDragEnter);
    this.$el.addEventListener('dragleave', this.onDragLeave);

    // Add it to jsplumb's list of managed elements to make it draggable
    jspSingleton.instance.manage(this.$el);

    if (this.isSelected) {
      jspSingleton.instance.addToDragSelection(this.$el);
    }
    // Whenever a resize happens revalidate this element
    resizeObserverWrapper.add(this.$el, () => {
      this.$nextTick(() => {
        jspSingleton.instance.revalidate(this.$el);
        // Re-make the branch a target if a an else or elseIf temporarily disabled it
        // this.makeTarget();
      });
    });
  },

  /**
   * Lifecycle hook, before destroy
   */
  beforeUnmount() {
    console.log('BoadrdBranch::beforeUnmount()');
    this.$el.removeEventListener('dragenter', this.onDragEnter);
    this.$el.removeEventListener('dragleave', this.onDragLeave);

    // Clean up conditions here
    const { children } = this.$refs.right;
    Array.from(children).forEach(el => {
      resizeObserverWrapper.remove(el);
      // jspSingleton.instance.remove(el);
    });

    console.log('UNMAKE TARGET on branch (beforeUnmount):', this.id);
    // jspSingleton.instance.unmakeTarget(this.$el);
    resizeObserverWrapper.remove(this.$el);

    // Remove it from jsplumb's list of managed elements
    jspSingleton.instance.unmanage(this.$el, false);
  },

  methods: {
    ...mapActions('project/config/boards', ['setOpen']),
    ...mapActions('application/drag', ['setTarget']),
    ...mapActions('application/contextToolbar', [
      'setAnchor',
      'recalcAnchorRect',
    ]),
    ...mapActions('project/data/elements', { updateElement: 'customUpdate' }),
    ...mapActions('project/data/jumpers', { updateJumper: 'customUpdate' }),
    ...mapActions('project/data/notes', { updateNote: 'customUpdate' }),
    ...mapActions('project/data/branches', { updateBranch: 'customUpdate' }),
    ...mapActions('project/data/comments', { updateComment: 'customUpdate' }),
    ...mapActions('project/data/branches', [
      'addElseIfCondition',
      'addElseCondition',
      'deleteElseCondition',
    ]),
    ...mapActions('project/config/branches', {
      selectBranch: 'select',
      deselectBranch: 'deselect',
    }),
    ...mapActions('project/config', ['deselectAll', 'deleteSelections']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
    ]),
    ...mapMutations('application/appStates', ['setShowSelections']),

    /**
     * Event handler for custom enableBranch event
     */
    onEnableBranch() {
      jspSingleton.instance.setDraggable(this.$el, true);
    },

    /**
     * Event handler for custom disableBranch event
     */
    onDisableBranch() {
      jspSingleton.instance.setDraggable(this.$el, false);
    },

    /**
     * Event listener - mouse up event on branch. We will handle branch selection here.
     * @param {MouseEvent}  event
     */
    onMouseUp(event) {
      const { button, ctrlKey, shiftKey, metaKey } = event;

      const isDragged = jspSingleton.instance.hasClass(this.$el, 'jtk-drag');
      // Perform the selection sequence if left single click and user can edit
      if (!isDragged && this.canEdit && button !== 1) {
        if (!this.$el.contains(document.activeElement)) {
          this.removeAllRanges();
        }

        // When Ctrl or Shift pressed toggle selection
        if (ctrlKey || shiftKey || metaKey) {
          if (!this.isSelected) {
            this.selectBranch({ id: this.id, apply: true });
          } else {
            this.deselectBranch({ id: this.id, apply: true });
          }
        } else if (!this.isSelected) {
          // Clear any selections and select only this branch
          this.deselectAll({ apply: true });
          jspSingleton.instance.clearDragSelection();

          this.selectBranch({ id: this.id, apply: true });
        }
      }
    },

    /**
     * Event hanlder - element mouse down event.
     * @param {MouseEvent} event
     */
    onMouseDown(event) {
      // On middle-click do not start drag
      if (event.button === 1) {
        event.preventDefault();
      }
    },

    /**
     * Event listener - contextmenu event.
     */
    onContextMenu(event) {
      if (!this.canEdit) return;
      // Select the branch
      if (!this.isSelected) {
        this.deselectAll({ apply: false });
        // Clear the selections from jsplumb
        jspSingleton.instance.clearDragSelection();
        this.selectBranch({ id: this.id, apply: true });
      }
      // Show the context menu
      this.createContextMenu(event.clientX, event.clientY);
    },

    /**
     * Create a context menu for this branch
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      this.createItem({
        name: 'Add elseif',
        icon: null,
        commands: {
          doAction: () => {
            this.addElseIfCondition({ id: this.id, index: '-', apply: true });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });
      if (!this.branch.conditions.elseCondition) {
        this.createItem({
          name: 'Add else',
          icon: null,
          commands: {
            doAction: () => {
              this.addElseCondition({ id: this.id, apply: true });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      } else {
        this.createItem({
          name: 'Remove else',
          icon: null,
          commands: {
            doAction: () => {
              this.deleteElseCondition({ id: this.id, apply: true });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      }

      this.createItem({
        name: 'Color',
        showName: false,
        icon: null,
        commands: {
          doAction: () => {},
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        addon: {
          component: 'color-picker',
          props: {
            selectedColor: this.branch.theme,
            onChangeColor: () => {
              this.setShowSelections({ value: false });
              clearTimeout(this.showSelectionsTimer);
              this.showSelectionsTimer = setTimeout(
                () => this.setShowSelections({ value: true }),
                1600
              );
            },
          },
        },
      });

      this.createItem({
        name: 'Delete selection',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteSelections({ apply: true });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },

    /**
     * Event listener - dragenter event something is dragged inside the element
     */
    onDragEnter(event) {
      this.dragEnter = true;
    },

    /**
     * Event listener - dragleave event something dragged is leaving the element
     */
    onDragLeave(event) {
      const rect = this.$el.getBoundingClientRect();
      if (
        event.clientY < rect.top ||
        event.clientY >= rect.bottom ||
        event.clientX < rect.left ||
        event.clientX >= rect.right
      ) {
        this.dragEnter = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.branch {
  position: absolute;
  box-sizing: border-box;
  font-size: 16px;
  color: #bcbcbc;
  display: flex;
  flex-flow: row;
  // background: #354042;
  // border-radius: 0 8px 8px 0;
  padding: 0;
  // backface-visibility: hidden;
  .left {
    width: 20px;
    border-radius: 8px 0 0 8px;
  }
  .right {
    display: flex;
    flex-flow: column;
  }
}

.branch :deep(.condition) {
  &:not(:first-child) {
    border-top: 2px solid #2c3233;
    // margin-top: -1px;
  }
  &:first-child {
    border-top-right-radius: 8px;
  }
  &:last-child {
    border-bottom-right-radius: 8px;
  }
}

.branch.default .left {
  background: #354042;
}

.branch.red .left {
  background: #571400;
}

.branch.blue .left {
  background: #002e6b;
}

.branch.purple .left {
  background: #540093;
}

.branch.cyan .left {
  background: #006557;
}

.branch.green .left {
  background: #00500b;
}

.branch.pink .left {
  background: #69003f;
}

.branch.gold .left {
  background: #5d5a00;
}

.branch.orange .left {
  background: #742b00;
}

.branch.lightBlue .left {
  background: #005186;
}

.branch.brown .left {
  background: #563400;
}

.branch.moss .left {
  background: #386300;
}

.branch.jtk-drag-selected.jtk-drag-hover::before,
.branch.jtk-drag-hover::before {
  content: '';
  z-index: -1;
  position: absolute;
  left: -3px;
  top: -3px;
  right: -3px;
  bottom: -3px;
  border: none;
  background: #db841e;
  border-radius: 8px;
  // background-image: repeating-linear-gradient(
  //   45deg,
  //   transparent,
  //   transparent 10px,
  //   rgba(30, 33, 33, 1) 10px,
  //   rgba(30, 33, 33, 1) 20px
  // );
  // background-size: 30px;
  // animation: spin 10s linear infinite;
}

.branch.jtk-drag-selected.jtk-drag-hover::after {
  display: none;
}

@keyframes spin {
  100% {
    background-position: 500px 0;
  }
}
</style>
